import { group, sum } from 'radash'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'

export type CartItem = {
  productId: string
  quantity: number
}

type CartContext = {
  products: CartItem[]
  addItem: (productId: string) => void
  removeItem: (productId: string) => void
  reset: () => void
}

const context = createContext<CartContext>({
  products: [],
  addItem: () => {},
  removeItem: () => {},
  reset: () => {},
})

function loadPersistedCart(cartId: string) {
  if (typeof window === 'undefined') {
    return []
  }
  return JSON.parse(window?.localStorage?.getItem(cartId) ?? '[]')
}

export function CartProvider({
  campaignId,
  children,
}: {
  campaignId: string
  children: React.ReactNode
}) {
  const cartId = useMemo(() => `cart-${campaignId}`, [campaignId])
  const [products, setProducts] = useState<CartItem[]>(loadPersistedCart(cartId))

  useEffect(() => {
    window?.localStorage?.setItem(cartId, JSON.stringify(products))
  }, [products, cartId])

  const addItem = (productId: string) => {
    const groupedCart = group([...products, { productId, quantity: 1 }], (x) => x.productId)
    const newCart = Object.entries(groupedCart).map(([productId, items]) => ({
      productId,
      quantity: sum(items ?? [], (x) => x.quantity),
    }))
    setProducts(newCart)
  }

  const removeItem = (productId: string) => {
    const newCart = products.slice().reduce((acc, curr) => {
      if (curr.productId === productId) {
        const nextQuantity = curr.quantity - 1
        if (nextQuantity > 0) {
          acc.push({ ...curr, quantity: nextQuantity })
        }
        return acc
      }
      acc.push(curr)
      return acc
    }, [] as CartItem[])
    setProducts(newCart)
  }

  const reset = () => {
    setProducts([])
  }

  return (
    <context.Provider value={{ products, addItem, removeItem, reset }}>{children}</context.Provider>
  )
}

export const useCart = () => useContext(context)
